import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Calendar, BookOpen } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DOMPurify from "dompurify";
import axios from "axios";
import { usePageTracking, trackBlogRead } from "@/hooks/useTracking";
import { usePageSeo, BASE_URL } from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogDetailPage() {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const goToBlogs = (e) => {
    e.preventDefault();
    navigate("/");
    const tryScroll = (attempts = 0) => {
      const el = document.querySelector("#blog");
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top: y, behavior: "smooth" });
      } else if (attempts < 15) {
        setTimeout(() => tryScroll(attempts + 1), 200);
      }
    };
    setTimeout(() => tryScroll(), 200);
  };

  usePageTracking(`/blog/${postId}`);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/blog/${postId}`)
      .then(res => {
        setPost(res.data);
        trackBlogRead(postId);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [postId]);

  const blogSlug = post?.seo?.slug || postId;

  const blogJsonLd = useMemo(() => {
    if (!post) return null;
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.seo?.meta_description || post.excerpt,
          "image": post.image_url,
          "datePublished": post.date,
          "author": {
            "@type": "Person",
            "name": "Gregor Scheiwiller",
            "url": BASE_URL
          },
          "publisher": {
            "@type": "Organization",
            "name": "LAB336",
            "url": BASE_URL,
            "logo": `${BASE_URL}/favicon.svg`
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `${BASE_URL}/blog/${blogSlug}`
          },
          "wordCount": post.word_count || undefined,
          "keywords": post.seo?.keywords?.join(", ") || undefined
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${BASE_URL}/#blog` },
            { "@type": "ListItem", "position": 3, "name": post.title, "item": `${BASE_URL}/blog/${blogSlug}` }
          ]
        }
      ]
    };
  }, [post, blogSlug]);

  usePageSeo({
    title: post ? `${post.title} | LAB336 Blog` : "Blog | LAB336",
    description: post?.seo?.meta_description || post?.excerpt || "",
    canonical: `${BASE_URL}/blog/${blogSlug}`,
    type: "article",
    jsonLd: blogJsonLd,
  });

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("de-CH", { day: "numeric", month: "long", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-lab-bg flex items-center justify-center">
        <div className="animate-pulse text-lab-text-muted">Laden...</div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-lab-bg flex flex-col items-center justify-center gap-6">
        <p className="text-lab-text-muted">Artikel nicht gefunden.</p>
        <a href="/#blog" onClick={goToBlogs} className="btn-primary rounded-none px-6 py-3 flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Zurück zur Homepage
        </a>
      </div>
    );
  }

  const hasContent = post.content_html || post.content_markdown;

  return (
    <div className="min-h-screen bg-white" data-testid="blog-detail-page">
      {/* Header */}
      <header className="bg-lab-primary text-white py-4 px-6 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-playfair text-xl tracking-wide">
            LAB<span className="text-lab-accent">336</span>
          </Link>
          <a href="/#blog" onClick={goToBlogs} className="text-sm text-white/80 hover:text-white flex items-center gap-1 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Alle Artikel
          </a>
        </div>
      </header>

      {/* Hero Image */}
      {post.image_url && (
        <div className="w-full h-64 md:h-96 overflow-hidden bg-lab-bg-light">
          <img
            src={post.image_url}
            alt={post.title}
            loading="lazy"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80";
            }}
          />
        </div>
      )}

      {/* Article Content */}
      <motion.article
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-3xl mx-auto px-6 py-12"
      >
        <div className="mb-6">
          <span className="bg-lab-accent/20 text-lab-primary px-3 py-1 text-xs font-semibold tracking-wide">
            {post.category}
          </span>
        </div>

        <h1 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-semibold text-lab-primary leading-tight mb-4" data-testid="blog-title">
          {post.title}
        </h1>

        {post.topic && post.topic !== post.title && (
          <p className="text-lg text-lab-text-muted mb-6">{post.topic}</p>
        )}

        <div className="flex items-center gap-6 text-sm text-lab-text-muted mb-10 pb-10 border-b border-lab-border">
          {post.date && (
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" /> {formatDate(post.date)}
            </span>
          )}
          {post.read_time && (
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" /> {post.read_time}
            </span>
          )}
          {post.word_count > 0 && (
            <span className="flex items-center gap-1.5">
              <BookOpen className="w-4 h-4" /> {post.word_count.toLocaleString()} Wörter
            </span>
          )}
        </div>

        {/* Article Body */}
        {hasContent ? (
          post.content_html ? (
            <div
              className="prose prose-lg max-w-none text-lab-text prose-headings:font-playfair prose-headings:text-lab-primary prose-a:text-lab-accent prose-strong:text-lab-primary"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content_html) }}
              data-testid="blog-content"
            />
          ) : (
            <div className="prose prose-lg max-w-none text-lab-text prose-headings:font-playfair prose-headings:text-lab-primary prose-a:text-lab-accent prose-strong:text-lab-primary" data-testid="blog-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {post.content_markdown}
              </ReactMarkdown>
            </div>
          )
        ) : (
          <div className="text-lab-text leading-relaxed text-lg space-y-6" data-testid="blog-excerpt">
            <p>{post.excerpt}</p>
          </div>
        )}

        {/* Back link */}
        <div className="mt-16 pt-8 border-t border-lab-border">
          <a
            href="/#blog"
            onClick={goToBlogs}
            className="text-lab-accent font-medium flex items-center gap-2 hover:gap-3 transition-all"
          >
            <ArrowLeft className="w-4 h-4" /> Zurück zu allen Artikeln
          </a>
        </div>
      </motion.article>
    </div>
  );
}
